import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../baseQuery';
import { ICommunityResource, IResourceResponse, IResourceFormValues, IFile, RNFilePart } from '@/types/admin/resources';

// Helper function to convert form data to FormData for resources API (matching admin-web exactly)
export const convertToFormData = (
  formValues: IResourceFormValues,
  propertyId: number | undefined,
): FormData => {
  // Input validation
  if (!formValues || typeof formValues !== 'object') {
    throw new Error('Invalid form values provided');
  }

  if (!propertyId || propertyId <= 0) {
    throw new Error('Valid property ID is required');
  }

  const { name, icon, description, content, url, files } = formValues;

  // Validate required fields
  if (!name || typeof name !== 'string' || name.trim().length === 0) {
    throw new Error('Resource name is required');
  }

  if (!icon || typeof icon !== 'string') {
    throw new Error('Resource icon is required');
  }

  const formData = new FormData();
  formData.append("name", name.trim());
  formData.append("icon", icon);
  formData.append("description", (description || '').trim());
  formData.append("content", (content || '').trim());
  formData.append("propertyId", propertyId.toString());
  formData.append("url", (url || '').trim());
  formData.append("position", (formValues.position || 0).toString());

  // Safely handle files array (support RN file parts and web File objects)
  if (files && Array.isArray(files)) {
    files.forEach((file: IFile, index: number) => {
      const maybePart = (file as any).file;
      // If provided a real File (web)
      if (maybePart && typeof (maybePart as any).arrayBuffer === 'function') {
        formData.append('files', maybePart as unknown as Blob);
        return;
      }

      // If provided an RN file part (uri/type/name)
      if (maybePart && (maybePart as any).uri) {
        const part = maybePart as RNFilePart;
        formData.append('files', part as unknown as Blob);
        return;
      }
    });
  }

  return formData;
};

// RTK Query API slice for resources
export const resourcesApi = createApi({
  reducerPath: 'resourcesApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Resources'],
  endpoints: (builder) => ({
    // Get resources list (matching admin-web exactly)
    listResources: builder.query<IResourceResponse, URLSearchParams>({
      query: (searchParams: URLSearchParams) => ({
        url: `/api/v1/resources/?${searchParams.toString()}`,
        method: 'GET',
      }),
      providesTags: ['Resources'],
    }),

    // Create resource
    createResource: builder.mutation<{ success: boolean; response: ICommunityResource }, FormData>({
      query: (formData) => ({
        url: '/api/v1/resources/',
        method: 'POST',
        body: formData,
        formData: true,
      }),
      invalidatesTags: ['Resources'],
    }),

    // Edit resource
    editResource: builder.mutation<{ success: boolean }, FormData>({
      query: (formData) => ({
        url: '/api/v1/resources/edit',
        method: 'PATCH',
        body: formData,
        formData: true,
      }),
      invalidatesTags: ['Resources'],
    }),

    // Delete resource
    deleteResource: builder.mutation<{ success: boolean }, ICommunityResource>({
      query: (resource) => ({
        url: '/api/v1/resources/',
        method: 'DELETE',
        body: resource,
      }),
      invalidatesTags: ['Resources'],
    }),

    // Update resource positions (for drag and drop)
    updateResourcePositions: builder.mutation<{ success: boolean }, FormData>({
      query: (formData) => ({
        url: '/api/v1/resources/update-positions',
        method: 'PATCH',
        body: formData,
        formData: true,
      }),
      invalidatesTags: ['Resources'],
    }),
  }),
});

// Export hooks for usage in functional components
export const {
  useListResourcesQuery,
  useLazyListResourcesQuery,
  useCreateResourceMutation,
  useEditResourceMutation,
  useDeleteResourceMutation,
  useUpdateResourcePositionsMutation,
} = resourcesApi;
